import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Slide,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchieChatMessage from "./ArchieChatMessage";
import ArchieConversationHistory from "./ArchieConversationHistory";
import { ArchieChatMessage as ArchieChatMessageType } from "../../types/archie/archie";

interface Props {
  open: boolean;
  messages: ArchieChatMessageType[];
  isLoading: boolean;
  customerId: string;
  onSend: (text: string) => void;
  onClose: () => void;
  onLoadConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

const ArchieChatWindow = ({
  open,
  messages,
  isLoading,
  customerId,
  onSend,
  onClose,
  onLoadConversation,
  onNewConversation,
}: Readonly<Props>) => {
  const [inputValue, setInputValue] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change or window opens
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    onLoadConversation(conversationId);
    setShowHistory(false);
  };

  const handleNewConversation = () => {
    onNewConversation();
    setShowHistory(false);
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 88,
          right: 24,
          zIndex: 1100,
          width: 400,
          height: 500,
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            backgroundColor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {showHistory ? "Chat History" : "Archie"}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={() => setShowHistory(!showHistory)}
              sx={{ color: "white", mr: 0.5 }}
              aria-label={showHistory ? "Back to chat" : "View chat history"}
            >
              {showHistory ? (
                <ArrowBackIcon fontSize="small" />
              ) : (
                <HistoryIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "white" }}
              aria-label="Close chat"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {showHistory ? (
          <ArchieConversationHistory
            customerId={customerId}
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
          />
        ) : (
          <>
            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1.5,
                backgroundColor: "grey.50",
              }}
            >
              {messages.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 4 }}
                >
                  Hi! I'm Archie. Ask me to find interactions — for example,
                  "Find all calls for agent John Smith in January 2024."
                </Typography>
              )}

              {messages.map((msg) => (
                <ArchieChatMessage key={msg.id} message={msg} />
              ))}

              {isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      backgroundColor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} sx={{ color: "white" }} />
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Thinking...
                    </Typography>
                  </Box>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1.5,
                py: 1,
                borderTop: 1,
                borderColor: "divider",
                backgroundColor: "white",
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Ask Archie..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                sx={{ mr: 1 }}
                inputProps={{ "aria-label": "Chat message" }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                aria-label="Send message"
              >
                <SendIcon />
              </IconButton>
            </Box>
          </>
        )}
      </Paper>
    </Slide>
  );
};

export default ArchieChatWindow;
